interface WritableFile {
  write(data: Blob): Promise<void>
  close(): Promise<void>
}

interface SaveFileHandle {
  createWritable(): Promise<WritableFile>
}

interface SaveFilePickerOptions {
  suggestedName: string
  types: Array<{
    description: string
    accept: Record<string, string[]>
  }>
}

type SaveFilePicker = (options: SaveFilePickerOptions) => Promise<SaveFileHandle>
type CsvDestination = (blob: Blob) => Promise<void>

function browserDownload(fileName: string): CsvDestination {
  return async (blob) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.style.display = 'none'
    document.body.append(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
  }
}

async function chooseDestination(fileName: string): Promise<CsvDestination | null> {
  const picker = (window as typeof window & { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker
  if (!picker) return browserDownload(fileName)

  try {
    const handle = await picker({
      suggestedName: fileName,
      types: [{ description: 'CSV file', accept: { 'text/csv': ['.csv'] } }],
    })
    return async (blob) => {
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null
    return browserDownload(fileName)
  }
}

/** Opens the save destination while the click still has user activation, then loads and writes the CSV. */
export async function saveTripCsv(
  tripId: string,
  load: () => Promise<Blob>,
): Promise<'saved' | 'cancelled'> {
  const destination = await chooseDestination(`mapapal-trip-${tripId}.csv`)
  if (!destination) return 'cancelled'
  const blob = await load()
  if (blob.size === 0) throw new Error('The CSV file was empty.')
  await destination(blob)
  return 'saved'
}
